/**
 * Chart candle helpers on top of the price service's `/v1/candles`.
 */

import type { CandlePoint } from "@/lib/live-candles/candle.types";
import type { ServiceCandle } from "./price-service.types";

/** Same cap as `MAX_CANDLES_PER_REQUEST` in price-service-rest. */
const MAX_LIMIT = 1000;
/** Venues asked for candles at the same time while looking for a source. */
export const MAX_SOURCES_IN_FLIGHT = 2;
/** A 429 is retried once after 1–2 s before it counts as a rate limit. */
const RATE_LIMIT_RETRY_MIN_MS = 1_000;
const RATE_LIMIT_RETRY_JITTER_MS = 1_000;

export function toCandlePoint(candle: ServiceCandle): CandlePoint {
  return {
    startTime: candle.time * 1000,
    openPrice: String(candle.open),
    highPrice: String(candle.high),
    lowPrice: String(candle.low),
    closePrice: String(candle.close),
    volume: String(candle.volume),
  };
}

/**
 * `/v1/candles` takes an end time and a count. Covers [startTime, endTime]
 * (ms); `end` is left out when the window reaches the current candle.
 */
export function candleWindowRequest(
  startTime: number,
  endTime: number,
  intervalMs: number,
  now = Date.now()
): { limit: number; end?: number } {
  const span = Math.max(0, endTime - startTime);
  const limit = Math.max(1, Math.min(MAX_LIMIT, Math.ceil(span / intervalMs) + 1));
  if (endTime >= now - intervalMs) return { limit };
  return { limit, end: Math.floor(endTime / 1000) };
}

export type CandleSourceResolution<S> =
  | { status: "candles"; source: S; candles: CandlePoint[] }
  | { status: "unavailable" }
  | { status: "rate_limited" };

export interface ResolveCandleSourceParams<S> {
  /** Tried in this order, two at a time; the first with candles in the window wins. */
  sources: S[];
  intervalMs: number;
  startTime: number;
  endTime: number;
  /** When every window is empty, load the latest `recentLimit` candles instead. */
  allowRecentFallback?: boolean;
  recentLimit: number;
  fetchWindow: (
    source: S,
    startTime: number,
    endTime: number
  ) => Promise<CandlePoint[]>;
  now?: () => number;
  /** Pause before retrying a 429 (default 1–2 s). */
  rateLimitRetryMs?: number;
}

export function isRateLimitError(error: unknown): boolean {
  const lower = (error instanceof Error ? error.message : "").toLowerCase();
  return (
    lower.includes("429") ||
    lower.includes("rate limit") ||
    lower.includes("too many")
  );
}

type WindowResult = CandlePoint[] | "rate_limited";

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** One venue's window; a 429 is retried once after a short pause. */
async function fetchSourceWindow<S>(
  params: ResolveCandleSourceParams<S>,
  source: S,
  startTime: number,
  endTime: number
): Promise<WindowResult> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await params.fetchWindow(source, startTime, endTime);
    } catch (error) {
      if (!isRateLimitError(error)) return [];
      if (attempt >= 1) return "rate_limited";
      await sleep(
        params.rateLimitRetryMs ??
          RATE_LIMIT_RETRY_MIN_MS + Math.random() * RATE_LIMIT_RETRY_JITTER_MS
      );
    }
  }
}

/**
 * Tries the sources in order with at most MAX_SOURCES_IN_FLIGHT requests at
 * a time and stops at the first (in order) with candles in the window, so an
 * alert chart does not ask every venue at once.
 */
function loadPhase<S>(
  params: ResolveCandleSourceParams<S>,
  startTime: number,
  endTime: number
): Promise<CandleSourceResolution<S> | "empty"> {
  const { sources } = params;
  const results: Array<WindowResult | undefined> = new Array(sources.length);

  return new Promise((resolve) => {
    let next = 0;
    let inFlight = 0;
    let checked = 0;
    let sawRateLimit = false;
    let done = false;

    const advance = () => {
      while (checked < sources.length) {
        const candles = results[checked];
        if (candles === undefined) break;
        if (candles === "rate_limited") {
          sawRateLimit = true;
        } else {
          const inWindow = candles.filter(
            (candle) =>
              candle.startTime + params.intervalMs > startTime &&
              candle.startTime < endTime
          );
          if (inWindow.length > 0) {
            done = true;
            resolve({ status: "candles", source: sources[checked], candles: inWindow });
            return;
          }
        }
        checked += 1;
      }
      if (checked >= sources.length) {
        done = true;
        resolve(sawRateLimit ? { status: "rate_limited" } : "empty");
        return;
      }
      while (inFlight < MAX_SOURCES_IN_FLIGHT && next < sources.length) {
        const index = next;
        next += 1;
        inFlight += 1;
        void fetchSourceWindow(params, sources[index], startTime, endTime).then(
          (result) => {
            inFlight -= 1;
            results[index] = result;
            if (!done) advance();
          }
        );
      }
    };

    advance();
  });
}

/**
 * Finds the first source (in order) with candles in the window, or with
 * `allowRecentFallback` the latest candles. At most two venues are asked at
 * a time.
 */
export async function resolveCandleSource<S>(
  params: ResolveCandleSourceParams<S>
): Promise<CandleSourceResolution<S>> {
  if (params.sources.length === 0) return { status: "unavailable" };

  const window = await loadPhase(params, params.startTime, params.endTime);
  if (window !== "empty") return window;

  if (params.allowRecentFallback) {
    const now = (params.now ?? Date.now)();
    const recent = await loadPhase(
      params,
      now - params.intervalMs * params.recentLimit,
      now
    );
    if (recent !== "empty") return recent;
  }
  return { status: "unavailable" };
}
